import React, { useState, useEffect, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { GCAL_URL, loadGcalScript } from '@utils/gcal';
import { usePrefersReducedMotion } from '@hooks';
// import { email } from '@config';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  padding: 0;

  @media (max-width: 480px) and (min-height: 700px) {
    padding-bottom: 10vh;
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 10px;
    color: var(--slate);
    line-height: 0.9;
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }

  /* Contenedor y botón oculto del widget de Google (solo actúa de disparador). */
  .gcal-hidden,
  .gcal-hidden + button {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const gcalTarget = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  // Carga el botón oficial de Google (oculto) para poder abrir su popup.
  useEffect(() => {
    let cancelled = false;
    loadGcalScript()
      .then(calendar => {
        if (cancelled || !calendar || !gcalTarget.current) {
          return;
        }
        if (gcalTarget.current.dataset.loaded === 'true') {
          return;
        }
        calendar.schedulingButton.load({
          url: GCAL_URL,
          color: '#64ffda',
          label: 'Reservar una cita',
          target: gcalTarget.current,
        });
        gcalTarget.current.dataset.loaded = 'true';
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Al pulsar el botón del hero, dispara el clic del botón de Google (abre el popup).
  // Si el widget aún no cargó, lo carga en el momento y luego abre el popup.
  const openScheduler = async e => {
    e.preventDefault();
    try {
      const calendar = await loadGcalScript();
      if (
        calendar &&
        gcalTarget.current &&
        gcalTarget.current.dataset.loaded !== 'true'
      ) {
        calendar.schedulingButton.load({
          url: GCAL_URL,
          color: '#64ffda',
          label: 'Reservar una cita',
          target: gcalTarget.current,
        });
        gcalTarget.current.dataset.loaded = 'true';
      }
      const gButton = gcalTarget.current && gcalTarget.current.nextElementSibling;
      if (gButton && gButton.tagName === 'BUTTON') {
        gButton.click();
        return;
      }
    } catch (err) {
      // Ignora y usa el fallback.
    }
    // Fallback: abre la agenda en una pestaña nueva si el widget no está disponible.
    window.open(GCAL_URL, '_blank', 'noreferrer');
  };

  const one = <h1>Hola 👋 mi nombre es</h1>;
  const two = <h2 className="big-heading">Dayron Hernandez.</h2>;
  const three = <h3 className="big-heading">Estratega Tecnológico & Arquitecto de Soluciones.</h3>;
  const four = (
    <>
      <p>
        Cuento con más de 8 años de experiencia transformando negocios a través de la tecnología. Como CTO y consultor, ayudo a empresas de todos los tamaños a detectar necesidades críticas y convertirlas en soluciones robustas, integrales y escalables.
        {'  '+' '}
        <a href="https://www.linkedin.com/in/dayron-hernandez/" target="_blank" rel="noreferrer">
          Conecta conmigo en LinkedIn.
        </a>
      </p>
    </>
  );
  const five = (
    <>
      <button className="email-link" type="button" onClick={openScheduler}>
        Agenda una Consulta Estratégica
      </button>
      {/* Anclaje oculto donde Google inyecta su botón (disparador del popup). */}
      <div className="gcal-hidden" ref={gcalTarget} aria-hidden="true" />
    </>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
